// Updated AnalyticsView.js with unified state management

import React, { useTransition, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSharedData } from '../contexts/SharedDataContext';
import { Modal, Space, Typography, Button, notification } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import DateRangeSelector from './DateRangeSelector';
import AnalyticsCard from './AnalyticsCard';
import LazyAnalyticsCard from "./LazyAnalyticsCard";
import FilterSection from './FilterSection';
import ChartSection from './ChartSection';
import AddFilter from './AddFilter';
import CallDetailModal from "./CallDetailModal";
import ScorecardsSection from './ScorecardsSection';
import CallList from './CallList';
import ViewConfigModal from './ViewConfigModal';
import { CHART_PRESETS } from './chartUtils';
import { analyticsDefaults } from './analyticsDefaults';
import ImprovedConfigurationManager from './ImprovedConfigurationManager';
import { LoadingOutlined, CloseOutlined, RobotOutlined, CodeOutlined, WarningOutlined, ClockCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { Spin, Empty, Skeleton } from 'antd';
import CustomFieldsManager from './CustomFieldsManager';
import { debounce } from 'lodash';
import { DATE_PRESETS, applyPreset } from './datePresets';
import ReportExportManager from './ReportExportManager';
import DownloadDataButton from "./DownloadDataButton";
import HeaderTools from "./HeaderTools";
import isEqual from 'lodash/isEqual';
import VoiceAnalystWidget from "../voice-agent/VoiceAnalystWidget";
import CustomReportModal from './CustomReportModal';
import { useModal } from './ModalContext';
import SecureComponent from "../SecureComponent";
import {RBAC_ROLES} from "../../hooks/useRBAC";



// Custom spinner icon that matches Ant Design's style
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const { Title, Text } = Typography;

/* --- Helper Functions --- */
const prettifyFieldName = (str) => {
    // Handle fields with underscores that represent paths
    if (str.includes('_')) {
        // Split by underscore
        const parts = str.split('_');

        // Format each part
        const formattedParts = parts.map(part =>
            part.replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/^./, char => char.toUpperCase())
        );

        // For fields that had collisions resolved with paths
        if (parts[0] === 'questions') {
            // For questions paths, display last part first, then the path
            const leaf = formattedParts[formattedParts.length - 1];
            const path = formattedParts.slice(0, -1).join(' › ');
            return `${leaf} (${path})`;
        }

        // For other underscore-separated names, just join with a separator
        return formattedParts.join(' › ');
    }

    // Default behavior for simple field names
    return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, char => char.toUpperCase());
};

const inferFieldConfig = (data) => {
    if (!data || data.length === 0) return [];

    const fieldStats = {};
    data.forEach(record => {
        Object.keys(record).forEach(key => {
            if (!fieldStats[key]) {
                fieldStats[key] = {
                    values: new Set(),
                    types: new Set(),
                    nullCount: 0,
                    totalCount: 0,
                    numericValues: [] // Add storage for all numeric values
                };
            }
            const value = record[key];
            fieldStats[key].totalCount++;
            if (value === null || value === undefined) {
                fieldStats[key].nullCount++;
            } else {
                fieldStats[key].values.add(value);
                fieldStats[key].types.add(typeof value);

                // Store numeric values (both actual numbers and strings that can be parsed as numbers)
                if (typeof value === 'number') {
                    fieldStats[key].numericValues.push(value);
                } else if (typeof value === 'string' && !isNaN(value) && !isNaN(parseFloat(value))) {
                    fieldStats[key].numericValues.push(parseFloat(value));
                    fieldStats[key].types.add('number');
                }

                // Check for boolean-like strings
                if (typeof value === 'string') {
                    const lowerValue = value.toLowerCase();
                    if (['true', 'false', 'yes', 'no'].includes(lowerValue)) {
                        fieldStats[key].types.add('boolean');
                    }
                }
            }
        });
    });

    return Object.keys(fieldStats).map(key => {
        const stats = fieldStats[key];
        const uniqueValues = stats.values.size;
        const nonNullCount = stats.totalCount - stats.nullCount;

        let type = 'text';
        let isMetric = false;
        let searchable = false;

        // Helper function to safely get lowercase value
        const safeLowerCase = (val) => {
            return typeof val === 'string' ? val.toLowerCase() : String(val);
        };

        // Get the first value safely
        const firstValue = stats.values.size > 0 ? [...stats.values][0] : null;
        const firstValueIsString = typeof firstValue === 'string';

        // Check if it's a boolean field
        if (
            stats.types.has('boolean') ||
            (stats.types.has('string') &&
                uniqueValues <= 2 &&
                firstValueIsString &&
                ['true', 'false', 'yes', 'no'].includes(safeLowerCase(firstValue)))
        ) {
            type = 'boolean';
        }
        // Check if it's a numeric field
        else if (
            stats.types.has('number') ||
            (stats.types.has('string') &&
                [...stats.values].every(val =>
                    typeof val === 'string' && !isNaN(val) && !isNaN(parseFloat(val))
                ))
        ) {
            type = 'number';
            isMetric = true;
        }
        // Check if it's an enum field
        else if (uniqueValues < 100 && !stats.types.has('number')) {
            type = 'enum';
            searchable = true;
        }
        // Check if it's a date field based on name
        else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
            type = 'date';
        }

        const config = {
            id: key,
            title: prettifyFieldName(key),
            type,
            isMetric,
            searchable,
            defaultVisible: false,
        };

        if (type === 'enum') {
            config.values = [...stats.values].map(val => ({
                label: String(val), // Convert to string to ensure it's safe
                value: val,
                count: data.filter(item => item[key] === val).length,
            }));
        }

        if (type === 'number') {
            // Include numeric min, max, and average in the field config
            const validNumbers = stats.numericValues.filter(n => !isNaN(n));

            if (validNumbers.length > 0) {
                const min = Math.min(...validNumbers);
                const max = Math.max(...validNumbers);
                const sum = validNumbers.reduce((acc, val) => acc + val, 0);
                const avg = sum / validNumbers.length;

                config.values = [{ value: min }, { value: max }];
                config.maxValue = max; // Store the maximum value
                config.minValue = min; // Store the minimum value
                config.avgValue = avg; // Store the average value
            }
        }

        return config;
    });
};


const toBoolean = (value) => {
    if (typeof value === 'boolean') return value; // Direct boolean values
    if (typeof value === 'number') return value !== 0; // 1 is true, 0 is false
    if (typeof value === 'string') {
        const lowerValue = value.toLowerCase().trim();
        if (['true', 'yes', '1'].includes(lowerValue)) return true;
        if (['false', 'no', '0'].includes(lowerValue)) return false;
        return !!value; // Non-empty strings are true, empty strings are false
    }
    // For null, undefined, or other types, use JavaScript’s truthy/falsy rules
    return !!value;
};

const flattenObject = (obj, parentPath = [], visited = new Set()) => {
    // Check for null/undefined objects
    if (obj === null || obj === undefined) {
        return {};
    }

    // Handle circular references
    if (typeof obj === 'object' && !Array.isArray(obj)) {
        if (visited.has(obj)) {
            const pathStr = parentPath.join('_') || 'circular';
            return { [pathStr]: '[Circular Reference]' };
        }
        visited.add(obj);
    }

    // Result object to store flattened fields
    const result = {};

    // Process all keys in the object
    Object.keys(obj).forEach(key => {
        const value = obj[key];
        const currentPath = [...parentPath, key];

        // If value is null or undefined, add it with just the key name initially
        if (value === null || value === undefined) {
            result[key] = value;
            return;
        }

        // Handle arrays (keep intact by default)
        if (Array.isArray(value)) {
            result[key] = value;
            return;
        }

        // Recursively flatten nested objects
        if (typeof value === 'object') {
            const nestedObj = flattenObject(value, currentPath, new Set(visited));

            // Add each flattened field to our result
            Object.entries(nestedObj).forEach(([nestedKey, nestedValue]) => {
                result[nestedKey] = nestedValue;
            });
            return;
        }

        // For primitive values, just use the key name
        result[key] = value;
    });

    return result;
};

// Traditional flattening approach for collision resolution with prefixes
const flattenObjectTraditional = (obj, prefix = '', visited = new Set()) => {
    if (obj === null || obj === undefined) {
        return {};
    }

    if (typeof obj === 'object' && !Array.isArray(obj)) {
        if (visited.has(obj)) {
            return { [prefix || 'circular']: '[Circular Reference]' };
        }
        visited.add(obj);
    }

    return Object.keys(obj).reduce((acc, key) => {
        const prefixedKey = prefix ? `${prefix}_${key}` : key;
        const value = obj[key];

        if (value === null || value === undefined) {
            return { ...acc, [prefixedKey]: value };
        }

        if (Array.isArray(value)) {
            return { ...acc, [prefixedKey]: value };
        }

        if (typeof value === 'object') {
            return { ...acc, ...flattenObjectTraditional(value, prefixedKey, new Set(visited)) };
        }

        return { ...acc, [prefixedKey]: value };
    }, {});
};

const applyFilters = (data, activeFilters, fieldConfig) => {
    // Quick exit if no filters are active
    const activeFilterKeys = Object.keys(activeFilters).filter(
        key => activeFilters[key] && activeFilters[key].length > 0
    );

    if (activeFilterKeys.length === 0) return data;

    // Sort filters by selectivity (most selective first)
    // This helps eliminate records early in the process
    const sortedFilterKeys = [...activeFilterKeys].sort((a, b) => {
        // Fewer selected values typically means more selective
        return activeFilters[a].length - activeFilters[b].length;
    });

    // Process each filter incrementally
    return data.filter((record) => {
        return sortedFilterKeys.every(fieldId => {
            const selectedValues = activeFilters[fieldId];
            if (!selectedValues || selectedValues.length === 0) return true;

            const field = fieldConfig.find((f) => f.id === fieldId);
            if (!field) return true;

            // Existing filter logic but optimized...
            const recordValue = record[fieldId];

            // Add early returns for common cases
            if (field.type === 'boolean') {
                return selectedValues.includes(toBoolean(recordValue));
            }

            // For number ranges, use early bounds checking
            if (field.type === 'number') {
                const numericValue = Number(recordValue);
                if (isNaN(numericValue)) return false;

                if (selectedValues.length === 1) {
                    // Single value match
                    return numericValue === Number(selectedValues[0]);
                }

                // Range check with early exit
                const minVal = selectedValues[0] !== undefined ? Number(selectedValues[0]) : -Infinity;
                if (numericValue < minVal) return false;

                const maxVal = selectedValues[1] !== undefined ? Number(selectedValues[1]) : Infinity;
                return numericValue <= maxVal;
            }

            // Text/enum - use Set for faster lookups
            if (field.type === 'text' || field.type === 'enum') {
                if (recordValue === null || recordValue === undefined) return false;

                // For simple exact matches (most common case)
                const strValue = String(recordValue);

                // Fast path for exact matches
                if (!selectedValues.some(v => v.includes('*') || v.includes('?'))) {
                    const valueSet = new Set(selectedValues);
                    return valueSet.has(strValue);
                }

                // Only use regex for patterns with wildcards
                return selectedValues.some((pattern) => {
                    if (typeof pattern === 'string' && (pattern.includes('*') || pattern.includes('?'))) {
                        // Regex match - more expensive
                        const regexPattern = '^' + pattern
                                .replace(/\*/g, '.*')
                                .replace(/\?/g, '.')
                            + '$';
                        try {
                            const regex = new RegExp(regexPattern, 'i');
                            return regex.test(strValue);
                        } catch (e) {
                            return false;
                        }
                    }
                    return strValue === pattern;
                });
            }

            return selectedValues.includes(recordValue);
        });
    });
};

const applyFiltersOld = (data, activeFilters, fieldConfig) => {
    return data.filter((record) =>
        Object.entries(activeFilters).every(([fieldId, selectedValues]) => {
            if (!selectedValues || (Array.isArray(selectedValues) && selectedValues.length === 0)) return true;
            const field = fieldConfig.find((f) => f.id === fieldId);
            if (!field) return true;

            const recordValue = record[fieldId];

            if (field.type === 'boolean') {
                const coercedValue = toBoolean(recordValue);
                return selectedValues.includes(coercedValue);
            } else if (field.type === 'number') {
                const numericValue = Number(recordValue);
                if (isNaN(numericValue)) return false;

                let minVal = -Infinity;
                let maxVal = Infinity;

                if (Array.isArray(selectedValues)) {
                    // Expect [min, max] or [singleValue]
                    if (selectedValues.length === 2) {
                        minVal = Number(selectedValues[0]) || -Infinity;
                        maxVal = Number(selectedValues[1]) || Infinity;
                    } else if (selectedValues.length === 1) {
                        minVal = maxVal = Number(selectedValues[0]) || 0; // Exact match
                    }
                } else if (typeof selectedValues === 'string') {
                    // Handle "min,max" string
                    const [minStr, maxStr] = selectedValues.split(',').map(s => s.trim());
                    minVal = minStr ? Number(minStr) : -Infinity;
                    maxVal = maxStr ? Number(maxStr) : Infinity;
                }

                return numericValue >= minVal && numericValue <= maxVal;
            } else if (field.type === 'text' || field.type === 'enum') {
                // Handle string fields
                if (recordValue === null || recordValue === undefined) return false;
                const strValue = String(recordValue);

                // If selectedValues is an array, check each value
                return selectedValues.some((pattern) => {
                    // Convert wildcard to regex (e.g., "John*" -> /^John.*$/)
                    if (typeof pattern === 'string' && (pattern.includes('*') || pattern.includes('?'))) {
                        const regexPattern = '^' + pattern
                                .replace(/\*/g, '.*')  // * matches any characters
                                .replace(/\?/g, '.')    // ? matches one character
                            + '$';
                        try {
                            const regex = new RegExp(regexPattern, 'i'); // Case-insensitive
                            return regex.test(strValue);
                        } catch (e) {
                            console.error('Invalid regex pattern:', pattern, e);
                            return false; // Fallback for invalid patterns
                        }
                    }
                    // Exact match for non-wildcard values
                    return strValue === pattern;
                });
            }
            // Default behavior (exact match for other types)
            return selectedValues.includes(recordValue);
        })
    );
};
const processCallData = (call) => {
    try {
        // Store original ID and key properties
        const id = call.id;
        const timestamp = call.start_timestamp;

        // First create a base object with the main call properties
        const baseObject = {
            ...call,
            date: timestamp,
            rawDate: new Date(timestamp)
        };

        // Flatten questions without parent paths initially
        let questionsFlattened = {};
        if (call.questions && typeof call.questions === 'object') {
            questionsFlattened = flattenObject(call.questions);
        }

        // Check for collisions
        const collisions = new Set();
        const baseKeys = new Set(Object.keys(baseObject));

        Object.keys(questionsFlattened).forEach(key => {
            if (baseKeys.has(key)) {
                collisions.add(key);
            }
        });

        // If we have collisions, create a fully-qualified version for each one
        // by flattening questions again with proper path prefixing
        if (collisions.size > 0) {
            // Traditional flattening with prefixes for collision resolution
            const fullyQualifiedFields = flattenObjectTraditional(call.questions, 'questions');

            // For each collision, replace the flattened key with the fully qualified version
            collisions.forEach(collidingKey => {
                // Find the corresponding fully qualified key
                const qualifiedKey = Object.keys(fullyQualifiedFields).find(key =>
                    key.endsWith(`_${collidingKey}`) || key === `questions_${collidingKey}`
                );

                if (qualifiedKey) {
                    // Add the qualified field with its value
                    questionsFlattened[qualifiedKey] = fullyQualifiedFields[qualifiedKey];
                    // Remove the colliding key
                    delete questionsFlattened[collidingKey];
                }
            });

            if (process.env.NODE_ENV === 'development') {
                console.warn(`Resolved ${collisions.size} field name collisions by adding path prefixes:`,
                    Array.from(collisions));
            }
        }

        // Build final result with collision handling
        return {
            ...questionsFlattened,
            ...baseObject,
            id,
            start_timestamp: timestamp
        };
    } catch (error) {
        console.error('Error processing call data:', error);
        return {
            ...call,
            date: call.start_timestamp,
            rawDate: new Date(call.start_timestamp),
            _processingError: true
        };
    }
};

const AnalyticsView = ({ selectedCompanyNames }) => {
    const { showCallModal, hideCallModal } = useModal(); // Get the function to show call details
    //const [analystSidebarVisible, setAnalystSidebarVisible] = useState(false);
    const renderCount = useRef(0);
    const callListRef = useRef(null);
    const [forceFetchKey, setForceFetchKey] = useState(0);
    const previousState = useRef({});



    // Base state - this will hold the entire application state
    const [dateRange, setDateRange] = useState({
        start: new Date(new Date().setDate(new Date().getDate() - 2)),
        end: new Date(),
    });
    const [lastFetchedDateRange, setLastFetchedDateRange] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const isFromConfigUpdate = useRef(false);
    const [activeDatePreset, setActiveDatePreset] = useState('yesterday'); // Default to last 7 days
    const { socket } = useSharedData();

    // The unified configuration state - contains everything we want to save
    const [currentConfig, setCurrentConfig] = useState(analyticsDefaults);
    const [initialConfigLoaded, setInitialConfigLoaded] = useState(false);
    const [customFieldsModalVisible, setCustomFieldsModalVisible] = useState(false);

    // Internal UI state - needs to be synced with currentConfig

    const [expandedFilterId, setExpandedFilterId] = useState(null);
    const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);


    // Synchronized UI states - these are derived from currentConfig
    // but need to be managed separately for UI interactions
    const [activeFilters, setActiveFilters] = useState({});
    const [visibleFilterIds, setVisibleFilterIds] = useState([]);
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [chartGranularity, setChartGranularity] = useState('day');

    const [callExportModalVisible, setCallExportModalVisible] = useState(false);
    const [scorecardExportModalVisible, setScorecardExportModalVisible] = useState(false);

    // Constants for limits - add these near the top of the file
    const MAX_RECORDS_LIMIT = 10000; // Maximum number of records to load
    const LOAD_TIMEOUT_MS = 120000;  // 2 minutes in milliseconds

    // Add these state variables to the AnalyticsView component
    const [loadingAbortController, setLoadingAbortController] = useState(null);
    const [loadedRecordCount, setLoadedRecordCount] = useState(0);
    const [loadTimeoutId, setLoadTimeoutId] = useState(null);

    // AI analysis agent
    const [aiFilteredCalls, setAiFilteredCalls] = useState(null);
    const [aiFilterTitle, setAiFilterTitle] = useState('');
    const [aiFilterDescription, setAiFilterDescription] = useState('');


    const { getAccessTokenSilently } = useAuth0();
    const { selectedTenant } = useSharedData();
    const openExtendedExportModal = { current: null };

    /*const handleToggleAnalystSidebar = useCallback(() => {
        setAnalystSidebarVisible(prev => !prev);
    }, []);*/

    const hideAllModals = ()=> {
        hideCallModal();
        setAiReportModalVisible(false);
    }

    const handleCallClick = useCallback((call) => {
        if (call && call.id && showCallModal) {
            showCallModal(call.id);
        }
    }, [showCallModal]);

    useEffect(() => {
        return () => {
            if (loadingAbortController) {
                loadingAbortController.abort();
            }
            if (loadTimeoutId) {
                clearTimeout(loadTimeoutId);
            }
        };
    }, [loadingAbortController, loadTimeoutId]);

    const [aiReportModalVisible, setAiReportModalVisible] = useState(false);
    const [aiReportData, setAiReportData] = useState(null);
    const [aiReportTitle, setAiReportTitle] = useState('');

    const handleShowReportModal = useCallback((reportData, title) => {
        if (reportData) {
            setAiReportData(reportData);
            setAiReportTitle(title || reportData.title || 'AI Analysis Report');
            setAiReportModalVisible(true);
        }
    }, []);

    const handleCloseAiReportModal = useCallback(() => {
        setAiReportModalVisible(false);
        setAiReportData(null);
        setAiReportTitle('');
    }, []);

    const handleAiCallsList = useCallback(async (callIds, title, description) => {
        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.post('/api/calls-by-ids',
                { callIds },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': selectedTenant,
                    }
                }
            );

            const processedCalls = response.data.data.map(call => processCallData(call));
            setAiFilteredCalls(processedCalls);
            setAiFilterTitle(title);
            setAiFilterDescription(description);
            setData(processedCalls);
            setIsInitialized(true);
            hideAllModals();
            // Reset all filters
            setActiveFilters(
                Object.fromEntries(inferredFieldConfig.map((field) => [field.id, []]))
            );
            // Keep only default visible filters
            setVisibleFilterIds(
                inferredFieldConfig.filter((f) => f.defaultVisible).map((f) => f.id)
            );

            startTransition(() => {
                setDeferredActiveFilters({...activeFilters});
            });

            // Scroll to CallList after state updates are complete
            setTimeout(() => {
                if (callListRef.current) {
                    callListRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 1000);

        } catch (error) {
            console.error('Error loading AI filtered calls:', error);
            notification.error({
                message: 'Error Loading AI Results',
                description: 'Failed to load the AI-selected calls.',
                duration: 5
            });
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently, selectedTenant, activeFilters]);

    const clearAiFilter = useCallback(() => {
        setAiFilteredCalls(null);
        setAiFilterTitle('');
        setAiFilterDescription('');
        refreshData(); // This will reload the normal date-range data
    }, []);


    const forceSyncConfig = useCallback(() => {
        if (hasPendingChanges.current) return;
        hasPendingChanges.current = true;

        // Call the current version of syncUIStateToConfig from a ref
        syncUIStateToConfigRef.current();

        setTimeout(() => {
            hasPendingChanges.current = false;
        }, 50);
    }, []); // Empty dependency array

    const handleLaunchBatchReport = useCallback(() => {
        // Close any existing notifications
        notification.destroy();

        // Sync config first
        forceSyncConfig();

        // Use the shared function to open the extended export modal
        setTimeout(() => {
            if (openExtendedExportModal.current) {
                openExtendedExportModal.current('both');
            }
        }, 50);
    }, [forceSyncConfig]);


    const handleStopLoading = useCallback(() => {
        if (loadingAbortController && !loadingAbortController.signal.aborted) {
            loadingAbortController.abort('user-cancelled');
        }

        if (loadTimeoutId) {
            clearTimeout(loadTimeoutId);
            setLoadTimeoutId(null);
        }
    }, [loadingAbortController, loadTimeoutId]);


    useEffect(() => {


        const fetchData = async () => {

           console.log("Fetching " + JSON.stringify(dateRange));
            if(!initialConfigLoaded) {
                return;
            }

            /*if(JSON.stringify(lastFetchedDateRange) == JSON.stringify(dateRange)) {
                return;
            }

            console.log("ACTUALLY Fetching " + JSON.stringify(dateRange));
*/
            // Clean up any previous abort controller and timeout
            if (loadingAbortController) {
                loadingAbortController.abort();
            }
            if (loadTimeoutId) {
                clearTimeout(loadTimeoutId);
            }

            // Create a new AbortController for this request
            const abortController = new AbortController();
            setLoadingAbortController(abortController);

            // Track the current date range for comparison during async operations
            const currentDateRange = {
                start: dateRange.start.toISOString(),
                end: dateRange.end.toISOString(),
                companies: [...selectedCompanyNames].sort().join(',')
            };

            setLoading(true);
            setError(null);
            setLoadedRecordCount(0);

            // Store collected data in a local variable to process even if aborted
            let allData = [];
            let stopReason = null;

            // Set a timeout to prevent loading too long
            const timeoutId = setTimeout(() => {
                if (abortController && !abortController.signal.aborted) {
                    stopReason = 'timeout';
                    abortController.abort('timeout');
                }
            }, LOAD_TIMEOUT_MS);

            setLoadTimeoutId(timeoutId);

            try {
                let lastRecordId = null;
                let hasMore = true;

                while (hasMore && !abortController.signal.aborted) {
                    // Check if dateRange or companies have changed since we started loading
                    const newDateRange = {
                        start: dateRange.start.toISOString(),
                        end: dateRange.end.toISOString(),
                        companies: [...selectedCompanyNames].sort().join(',')
                    };

                    if (newDateRange.start !== currentDateRange.start ||
                        newDateRange.end !== currentDateRange.end ||
                        newDateRange.companies !== currentDateRange.companies) {
                        console.log("Date range or companies changed during loading, aborting current load");
                        stopReason = 'criteria-changed';
                        abortController.abort('criteria-changed');
                        break;
                    }



                    try {
                        const token = await getAccessTokenSilently();
                        const response = await axios.get('/api/analytics-data', {
                            params: {
                                startDate: dateRange.start.toISOString(),
                                endDate: dateRange.end.toISOString(),
                                companyNames: selectedCompanyNames,
                                pageSize: 1000,
                                lastRecordId
                            },
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'x-tenant': selectedTenant,
                            },
                            signal: abortController.signal
                        });

                        const pageData = response.data.data;
                        allData = allData.concat(pageData);
                        setLoadedRecordCount(allData.length);
                       // console.log("Got page data with  " + pageData.length);
                       // console.log("Alldata now " + allData.length);

                        // Check if we've reached the record limit
                        if (allData.length >= MAX_RECORDS_LIMIT) {
                            console.log("Record limit reached, stopping at", allData.length);
                            stopReason = 'limit-reached';
                            hasMore = false;
                            break;
                        }

                        hasMore = response.data.hasMore;
                        lastRecordId = (pageData && pageData.length > 0) ? pageData[pageData.length-1].id : null;
                    } catch (error) {
                        // If it's an abort error, we'll still process what we have
                        if (error.name === 'AbortError' || error.name === 'CanceledError' ||
                            (error.message && error.message.includes('canceled'))) {
                            console.log('Request aborted:', error.message);
                            // We'll break the loop but still process the data we have
                            break;
                        }
                        // For other errors, rethrow to be caught by the outer catch
                        throw error;
                    }
                }
                setLastFetchedDateRange(dateRange);
            } catch (err) {
                // Only set error if it's not an abort/cancel error
                if (err.name !== 'AbortError' && err.name !== 'CanceledError' &&
                    (!err.message || !err.message.includes('canceled'))) {
                    console.error('Error fetching analytics:', err);
                    setError(err.message);

                    notification.error({
                        message: 'Error Loading Data',
                        description: `Failed to load analytics data: ${err.message}`,
                        duration: 0
                    });
                }
            } finally {
                clearTimeout(timeoutId);
                setLoadTimeoutId(null);

                // Always process whatever data we've collected, even if loading was interrupted
               // if (allData.length > 0) {
                    try {
                        // Process each call with our smart flattening approach
                        const processedCalls = allData.map(call => processCallData(call));
                        const customFieldsProcessedCalls = processCustomFields(processedCalls);
                        setData(customFieldsProcessedCalls);
                        startTransition(() => {
                            setDeferredActiveFilters({...activeFilters});
                        });
                        console.log(`Set data with ${customFieldsProcessedCalls.length} calls. Alldata has ${allData.length}`);
                        setIsInitialized(true);

                        // Show appropriate notification based on the reason for stopping
                        if (stopReason === 'limit-reached') {
                            notification.warning({
                                message: 'Record Limit Reached',
                                description: (
                                    <div>
                                        The selected time span exceeds the maximum records that can be loaded ({MAX_RECORDS_LIMIT}).
                                        <Button
                                            type="link"
                                            onClick={handleLaunchBatchReport}
                                            style={{ padding: '0', height: 'auto', marginLeft: '8px' }}
                                        >
                                            Run Batch Report
                                        </Button>
                                    </div>
                                ),
                                duration: 0,
                                icon: <WarningOutlined style={{ color: '#faad14' }} />,
                                key: 'limit-reached'
                            });
                        } else if (stopReason === 'timeout') {
                            notification.warning({
                                message: 'Loading Timeout',
                                description: (
                                    <div>
                                        Loading took too long (over 2 minutes). Showing partial data.
                                        <Button
                                            type="link"
                                            onClick={handleLaunchBatchReport}
                                            style={{ padding: '0', height: 'auto', marginLeft: '8px' }}
                                        >
                                            Run Batch Report
                                        </Button>
                                    </div>
                                ),
                                duration: 0,
                                icon: <ClockCircleOutlined style={{ color: '#faad14' }} />,
                                key: 'load-timeout'
                            });
                        } else if (stopReason === 'user-cancelled' && loadedRecordCount > 0) {
                            notification.info({
                                message: 'Loading Stopped',
                                description: `Showing ${loadedRecordCount} records loaded so far.`,
                                duration: 5
                            });
                        }
                    } catch (processError) {
                        console.error('Error processing collected data:', processError);
                        notification.error({
                            message: 'Error Processing Data',
                            description: `Failed to process collected data: ${processError.message}`,
                            duration: 0
                        });
                    }
              //  }

                setLoading(false);
            }
        };


        fetchData();

    }, [selectedCompanyNames, getAccessTokenSilently, initialConfigLoaded,  forceFetchKey]);

    // When data loads, infer field configuration and initialize filters
    const inferredFieldConfig = useMemo(() => {
        if (!data || data.length === 0) return [];
        const inferred = inferFieldConfig(data);
        const merged = inferred.map((field) => {
            const filterDefaults = currentConfig.filters?.[field.id] || {};
            const metricDefaults = currentConfig.metrics?.[field.id] || {};
            return {
                ...field,
                title: filterDefaults.title || metricDefaults.title || field.title,
                defaultVisible: filterDefaults.defaultVisible !== undefined
                    ? filterDefaults.defaultVisible
                    : field.defaultVisible,
            };
        });
        return merged.sort((a, b) => a.title.localeCompare(b.title));
    }, [data, currentConfig]);


    const processCustomFields = (rawData) => {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) return rawData;

        // If there are no custom fields, return the original data
        if (!currentConfig.customFields || currentConfig.customFields.length === 0) {
            return rawData;
        }

        try {
            // Process each record
            return rawData.map(record => {
                // Clone the record to avoid mutating the original
                const processedRecord = { ...record };

                // Apply each custom field expression
                currentConfig.customFields.forEach(field => {
                    try {
                        if (field.expression) {
                            const evalFn = new Function('record', 'records', `return ${field.expression}`);
                            processedRecord[field.name] = evalFn(record, rawData);
                        }
                    } catch (error) {
                        console.error(`Error evaluating custom field "${field.name}":`, error);
                        // Set to null if evaluation fails
                        processedRecord[field.name] = null;
                    }
                });

                return processedRecord;
            });
        } catch (error) {
            console.error('Error processing custom fields:', error);
            return rawData;
        }
    };


    const [deferredActiveFilters, setDeferredActiveFilters] = useState({});
    const [isFiltering, setIsFiltering] = useState(false);
    const [isPending, startTransition] = useTransition();

    useEffect(() => {
        if (data && data.length > 0 && activeFilters && Object.keys(activeFilters).length > 0) {
            // When data loads and we have filters, ensure they're applied
            startTransition(() => {
                setDeferredActiveFilters({...activeFilters});
            });
        }
    }, [data, activeFilters]);

    const filteredData = useMemo(() => {
        if (!data) return [];
        return applyFilters(data, deferredActiveFilters, inferredFieldConfig);
    }, [data, deferredActiveFilters, inferredFieldConfig]);

    // Apply filters to data
    /*const filteredData = useMemo(() => {
        if (!data) return [];
        return applyFilters(data, activeFilters, inferredFieldConfig);
    }, [data, activeFilters, inferredFieldConfig]);
*/
    // When no metric pill is selected, default to "Call Volume"
    const availableMetrics = useMemo(() => {
        return inferredFieldConfig
            .filter((f) => f.isMetric)
            .map((f) => ({
                id: f.id,
                label: f.title,
            }));
    }, [inferredFieldConfig]);

    const metricsForChart = useMemo(() => {
        return availableMetrics.length > 0
            ? availableMetrics
            : [{ id: 'volume', label: 'Call Volume' }];
    }, [availableMetrics]);

    const handleScorecardFilterChange = (fieldName, values) => {
        // 1. Update activeFilters for immediate UI representation
        //    Using the original "complete replacement" logic for this specific field.
        setActiveFilters(prevActiveFilters => {
            const newActiveFilters = { ...prevActiveFilters };

            // Remove existing filter for this field first
            delete newActiveFilters[fieldName];

            // Then add the new filter values if they exist
            if (values && values.length > 0) {
                newActiveFilters[fieldName] = values;
            }
            return newActiveFilters;
        });

        // 2. Update deferredActiveFilters to trigger actual data filtering.
        //    This logic should mirror how activeFilters is updated for consistency.
        setIsFiltering(true); // Optional: Show a visual cue that filtering is in progress
        startTransition(() => {
            setDeferredActiveFilters(prevDeferredFilters => {
                const newDeferredFilters = { ...prevDeferredFilters };
                delete newDeferredFilters[fieldName]; // Mirror activeFilters logic

                if (values && values.length > 0) {
                    newDeferredFilters[fieldName] = values;
                }
                return newDeferredFilters;
            });
            setIsFiltering(false); // Hide visual cue
        });

        // 3. Update visibility of the filter in the FilterSection
        if (values && values.length > 0) {
            // If applying a filter (values have content), ensure the filter is visible
            if (!visibleFilterIds.includes(fieldName)) {
                setVisibleFilterIds(prevIds => [...prevIds, fieldName]);
            }
        } else {
            // If clearing a filter (values is null, undefined, or an empty array)
            // Check if the filter is a "defaultVisible" one. If not, hide it.
            // This mimics the behavior of FilterSection's onRemove or clearing a filter there.
            const fieldConfigItem = inferredFieldConfig.find(f => f.id === fieldName);
            if (fieldConfigItem && !fieldConfigItem.defaultVisible) {
                setVisibleFilterIds(prevIds => prevIds.filter(id => id !== fieldName));
            }
        }

        // The useEffect hook that watches activeFilters, visibleFilterIds, etc.,
        // for changes (and calls syncUIStateToConfig) should pick up these updates.
    };


     const hasPendingChanges = useRef(false);

    function useDeepCompareEffect(callback, dependencies) {
        const currentDependenciesRef = useRef();

        if (!currentDependenciesRef.current ||
            !isEqual(currentDependenciesRef.current, dependencies)) {
            currentDependenciesRef.current = dependencies;
        }

        useEffect(callback, [currentDependenciesRef.current]);
    }

    // Completely rewrite the effect to prevent loops
    useDeepCompareEffect(() => {
        // Only run when currentConfig changes and we're initialized with data
        if (!isInitialized || !data || data.length === 0) {
            return;
        }

        // Skip if this is a change originating from our own UI updates
        if (hasPendingChanges.current) {
            return;
        }



        // Prevent our state updates from triggering syncUIStateToConfig
        isFromConfigUpdate.current = true;

        // Store all updates to be performed
        const updates = {};

        // Batch state checks
        if (currentConfig.visibleFilterIds &&
            JSON.stringify(currentConfig.visibleFilterIds) !== JSON.stringify(visibleFilterIds)) {
            updates.visibleFilterIds = currentConfig.visibleFilterIds;
        }

        if (currentConfig.activeFilters &&
            JSON.stringify(currentConfig.activeFilters) !== JSON.stringify(activeFilters)) {
            updates.activeFilters = currentConfig.activeFilters;
        }

        if (currentConfig.chart?.metrics &&
            JSON.stringify(currentConfig.chart.metrics) !== JSON.stringify(selectedMetrics)) {
            updates.metrics = currentConfig.chart.metrics;
        }

        if (currentConfig.chart?.granularity && currentConfig.chart.granularity !== chartGranularity) {
            updates.granularity = currentConfig.chart.granularity;
        }

        if (currentConfig.dateRangePreset &&
            currentConfig.dateRangePreset !== activeDatePreset) {
            updates.datePreset = currentConfig.dateRangePreset;

            // Only compute new date range if preset is changing
            const newDateRange = applyPreset(currentConfig.dateRangePreset);
            if (newDateRange) {
                updates.dateRange = newDateRange;
            }
        }

        // Check if we have any updates to perform
        const hasUpdates = Object.keys(updates).length > 0;

        // Only perform updates if something actually changed
        if (hasUpdates) {
            // Apply all updates in a batch to minimize re-renders
            React.startTransition(() => {
                if (updates.visibleFilterIds) setVisibleFilterIds(updates.visibleFilterIds);
                if (updates.activeFilters) {
                    setActiveFilters(updates.activeFilters);
                    setDeferredActiveFilters({...activeFilters});
                }
                if (updates.metrics) setSelectedMetrics(updates.metrics);
                if (updates.granularity) setChartGranularity(updates.granularity);
                if (updates.datePreset) setActiveDatePreset(updates.datePreset);
                if (updates.dateRange) setDateRange(updates.dateRange);
            });
        }

        // Reset the flag after a delay to ensure all state updates complete
        const timeoutId = setTimeout(() => {
            isFromConfigUpdate.current = false;
        }, 100);

        // Clean up the timeout if the effect runs again
        return () => clearTimeout(timeoutId);
    }, [currentConfig, isInitialized, data]);

    const syncUIStateToConfig = useCallback(
        debounce(() => {

            // Skip if we're not initialized or missing data
            if (!isInitialized || !data || data.length === 0) return;

            // Skip if changes are from config itself or already updating
            if (isFromConfigUpdate.current || hasPendingChanges.current) {
                return;
            }

            // Compare current UI state with config state
            const needsUpdate =
                JSON.stringify(visibleFilterIds) !== JSON.stringify(currentConfig.visibleFilterIds) ||
                JSON.stringify(activeFilters) !== JSON.stringify(currentConfig.activeFilters) ||
                JSON.stringify(selectedMetrics) !== JSON.stringify(currentConfig.chart?.metrics) ||
                chartGranularity !== currentConfig.chart?.granularity ||
                activeDatePreset !== currentConfig.dateRangePreset;

            // Only update if something actually changed
            if (!needsUpdate) {
                return;
            }


            // Set flag that we're updating from UI state
            hasPendingChanges.current = true;

            // Update config with current UI state
            setCurrentConfig(prev => ({
                ...prev,
                filters: inferredFieldConfig.reduce((acc, field) => {
                    acc[field.id] = {
                        title: field.title,
                        defaultVisible: visibleFilterIds.includes(field.id),
                    };
                    return acc;
                }, {}),
                visibleFilterIds: visibleFilterIds,
                activeFilters: activeFilters,
                chart: {
                    ...prev.chart,
                    metrics: selectedMetrics,
                    granularity: chartGranularity,
                },
                dateRangePreset: activeDatePreset,
            }));

            // Clear the flag after a delay
            const timeoutId = setTimeout(() => {
                hasPendingChanges.current = false;
                          }, 100);

            return () => clearTimeout(timeoutId);
        }, 300),
        [
            activeFilters,
            selectedMetrics,
            chartGranularity,
            activeDatePreset,
            inferredFieldConfig,
            isInitialized,
            data,
            currentConfig
        ]
    );

    const handleFilterChange = (filterId, values) => {
        // Immediately update UI state
        setActiveFilters(prev => ({
            ...prev,
            [filterId]: values
        }));

        // Show filtering indicator
        setIsFiltering(true);

        // Defer the actual filtering operation
        startTransition(() => {
            setDeferredActiveFilters(prev => ({
                ...prev,
                [filterId]: values
            }));
            setIsFiltering(false);
        });
    };

    useEffect(() => {
        // Only run if we're initialized and not in the middle of a config update
        if (isInitialized && !isFromConfigUpdate.current) {
            syncUIStateToConfig();
        }
    }, [
        activeFilters,
        visibleFilterIds,
        selectedMetrics,
        chartGranularity,
        activeDatePreset,
        syncUIStateToConfig,
        isInitialized,
        isFromConfigUpdate
    ]);


// Modified filter visibility handler
    const handleFilterVisibilityChange = (filterId, visible) => {
        setVisibleFilterIds(prev => {
            return visible
                ? [...prev, filterId]
                : prev.filter(id => id !== filterId);
        });
        // Trigger sync to config
        syncUIStateToConfig();
    };

// Modified metric handlers
    const handleMetricAdd = (metric) => {
        setSelectedMetrics(prev => [...prev, metric]);
        syncUIStateToConfig();
    };

    const handleMetricRemove = (metricId) => {
        setSelectedMetrics(prev => prev.filter(m => m.id !== metricId));
        syncUIStateToConfig();
    };

    const handleMetricToggleAverage = (metricId) => {
        setSelectedMetrics(prev => prev.map(m =>
            m.id === metricId ? { ...m, showAverage: !m.showAverage } : m
        ));
        syncUIStateToConfig();
    };

    const handleGranularityChange = (granularity) => {
        setChartGranularity(granularity);
        syncUIStateToConfig();
    };

// Update date preset handler
    const handleDatePresetChange = (preset) => {
        // Update the active preset
        setActiveDatePreset(preset);

        // Important: Only calculate and set new date range if preset is not null
        if (preset !== null) {
            const newDateRange = applyPreset(preset);
            setDateRange(newDateRange);
            setCurrentConfig(prev => ({
                ...prev,
                dateRangePreset: preset,
                dateRange: newDateRange
            }));
            refreshData();
        } else {
            // For null preset (custom date), just update the preset in config
            // but DON'T change the dateRange until user applies their changes
            setCurrentConfig(prev => ({
                ...prev,
                dateRangePreset: null
            }));
        }
    };



    const handleCallListConfigChange = useCallback((newCallListConfig) => {
        setCurrentConfig(prev => ({
            ...prev,
            callList: newCallListConfig,
            activeFilters: activeFilters
        }));
    }, [activeFilters]);



    // Handle changes to scorecard configuration
    const handleScorecardConfigChange = (newScorecardConfig) => {
        setCurrentConfig(prev => ({
            ...prev,
            scorecards: newScorecardConfig,
            // Explicitly preserve the current filter states
            activeFilters: activeFilters,
            visibleFilterIds: visibleFilterIds
        }));
    };


    const handleConfigChange = (newConfig) => {
        console.log("Handle Config Change");
        // Prevent syncing back during these updates
        hasPendingChanges.current = false;
        let newDateRange;

          React.startTransition(() => {
            if (newConfig.dateRangePreset && newConfig.dateRangePreset !== activeDatePreset) {
                newDateRange = applyPreset(newConfig.dateRangePreset);
            }
            if (newDateRange && (JSON.stringify(newDateRange) != JSON.stringify(dateRange))) {
                setDateRange(newDateRange);
                setLastFetchedDateRange(null);
                refreshData();
            }

            setCurrentConfig(newConfig);
            setInitialConfigLoaded(true);

        });
    };

    const refreshData = () => setForceFetchKey(prev => prev + 1);

    const handleCustomFieldsUpdate = (updatedFields) => {
        // Update config with new custom fields
        setCurrentConfig(prev => ({
            ...prev,
            customFields: updatedFields
        }));

        // Re-process the data with the new custom fields
        if (data && data.length > 0) {
            // Create a new data array with custom fields applied
            const reprocessedData = processCustomFields([...data]);
            // Force re-inference by updating the data reference.
            setData(reprocessedData);
            // No need to update inferredFieldConfig manually since it's computed via useMemo
        }
    };

    // Add these at the top of your AnalyticsView component where other state is defined
    const handleShowCallExport = useCallback(() => {
        setCallExportModalVisible(true);
    }, []);

    const handleShowScorecardExport = useCallback(() => {
        setScorecardExportModalVisible(true);
    }, []);



// Keep a ref to the latest syncUIStateToConfig
    const syncUIStateToConfigRef = useRef(syncUIStateToConfig);
    useEffect(() => {
        syncUIStateToConfigRef.current = syncUIStateToConfig;
    }, [syncUIStateToConfig]);

    const memoizedConfigChange = useCallback((newConfig) => {
        handleConfigChange(newConfig);
    }, [handleConfigChange]);

    const memoizedShowCallExport = useCallback(() => {
        setCallExportModalVisible(true);
    }, []);

    const memoizedShowScorecardExport = useCallback(() => {
        setScorecardExportModalVisible(true);
    }, []);

    const memoizedConfig = useMemo(() => currentConfig, [currentConfig]);
    const memoizedDateRange = useMemo(() => dateRange, [dateRange]);

    const stableCallbacks = useRef({
        handleCallListConfigChange: (newCallListConfig) => {

        }
    });


    useEffect(() => {
        stableCallbacks.current = {
            handleCallListConfigChange: (newCallListConfig) => {
                setCurrentConfig(prev => ({
                    ...prev,
                    callList: newCallListConfig
                }));
            }
        };
    }, []);

    useEffect(() => {
        renderCount.current++;

        // Find what changed to cause this render
        const currentState = {
            loading,
            error,
            data: data?.length,
            hasMore,
            isInitialized,
            currentConfig: JSON.stringify(currentConfig),
            visibleFilterIds: JSON.stringify(visibleFilterIds),
            activeFilters: JSON.stringify(activeFilters),
            selectedMetrics: JSON.stringify(selectedMetrics),
            chartGranularity,
            activeDatePreset
        };

        // Compare with previous state to see what changed
        const changedKeys = Object.keys(currentState).filter(
            key => JSON.stringify(currentState[key]) !== JSON.stringify(previousState.current[key])
        );

        if (renderCount.current > 1) {
           // console.log(`AnalyticsView re-render #${renderCount.current}, changed: ${changedKeys.join(', ')}`);
        }

        // Update previous state
        previousState.current = currentState;
    }, [loading, error, data, hasMore, isInitialized, currentConfig, visibleFilterIds,
        activeFilters, selectedMetrics, chartGranularity, activeDatePreset]);

    const dataLoadingContent = (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 0',
            minHeight: '200px',   // Ensure minimum height for centering
            height: '100%',       // Take full height of container
            width: '100%',        // Take full width
            textAlign: 'center'
        }}>
            <Spin indicator={antIcon} />
            <div style={{ color: '#6b7280', marginTop: 8 }}>
                Loading analytics data... {loadedRecordCount > 0 ? `(${loadedRecordCount} records)` : ''}
            </div>
            <Button type="link" onClick={handleStopLoading}>Stop loading</Button>
        </div>
    );


    return (
        <div style={{ padding: '24px', overflowY: 'scroll' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Dashboard Header with improved configuration manager */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    <HeaderTools
                        currentConfig={memoizedConfig}
                        dateRange={memoizedDateRange}
                        onConfigChange={memoizedConfigChange}
                        onShowCallExport={memoizedShowCallExport}
                        onShowScorecardExport={memoizedShowScorecardExport}
                        onSyncConfig={forceSyncConfig}
                        openExtendedExportModal={openExtendedExportModal}
                    />
                    {/* AI Filter Banner */}
                    {aiFilteredCalls && (
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            margin: '0 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <RobotOutlined />
                            <div>
                                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{aiFilterTitle}</div>
                                {aiFilterDescription && (
                                    <div style={{ fontSize: '12px', opacity: 0.9 }}>{aiFilterDescription}</div>
                                )}
                                <div style={{ fontSize: '11px', opacity: 0.8 }}>
                                    {aiFilteredCalls.length} AI-selected calls
                                </div>
                            </div>
                            <Button
                                type="text"
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={clearAiFilter}
                                style={{ color: 'white' }}
                                title="Return to date range view"
                            />
                        </div>
                    )}
                    {!aiFilteredCalls && (
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <DateRangeSelector
                            value={dateRange}
                            onChange={(newRange) => {
                                handleStopLoading();
                                setDateRange(newRange);
                                if (aiFilteredCalls) clearAiFilter(); // Clear AI filter when changing dates
                                else refreshData();
                            }}
                            activePreset={activeDatePreset}
                            onPresetChange={(preset) => {
                                handleStopLoading();
                                handleDatePresetChange(preset);
                                if (aiFilteredCalls) clearAiFilter(); // Clear AI filter when changing preset
                            }}
                            disabled={!!aiFilteredCalls} // Disable when showing AI results
                        />
                    </div>)}
                </div>


                {/* Filters Section */}
                <AnalyticsCard>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Title level={4} style={{ margin: 0 }}>Filters</Title>
                        <Text style={{
                            marginLeft: '12px',
                            color: '#6b7280',
                            fontSize: '14px',
                            fontWeight: 'normal',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            {isPending ? (
                                <>
                                    <Spin size="small" style={{ marginRight: '8px' }} />
                                    <span>?? of {data?.length || 0} calls </span>
                                    <Spin size="small" style={{ marginLeft: '8px' }} />
                                </>
                            ) : (
                                <span>{filteredData.length} of {data?.length || 0} calls</span>
                            )}
                        </Text>
                        <div>
                        {Object.values(activeFilters).some((filter) => filter.length > 0) && (
                            <Button
                                type="link"
                                onClick={() => {
                                    // Reset all filters
                                    setActiveFilters(
                                        Object.fromEntries(inferredFieldConfig.map((field) => [field.id, []]))
                                    );
                                    // Keep only default visible filters
                                    setVisibleFilterIds(
                                        inferredFieldConfig.filter((f) => f.defaultVisible).map((f) => f.id)
                                    );
                                }}
                            >
                                Clear all filters
                            </Button>
                        )}
                        <Button
                            type="link"
                            icon={<CodeOutlined />}
                            onClick={() => setCustomFieldsModalVisible(true)}
                        >
                            Custom Fields {currentConfig.customFields?.length > 0 && `(${currentConfig.customFields.length})`}
                        </Button>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-8px' }}>
                        {
                            visibleFilterIds.map((filterId) => {
                            const field = inferredFieldConfig.find((f) => f.id === filterId);
                            if (!field) return null;
                            return (
                                <div key={filterId} style={{ padding: '8px' }}>
                                    <FilterSection
                                        title={field.title}
                                        filter={field}
                                        type={field.type}
                                        values={field.values}
                                        selectedValues={activeFilters[filterId]}
                                        onSelectionChange={(values) => {
                                            handleFilterChange(filterId, values);
                                            if (values.length === 0 && !field.defaultVisible) {
                                                handleFilterVisibilityChange(filterId, false);
                                            }
                                        }}
                                        searchable={field.searchable}
                                        expanded={expandedFilterId === field.id}
                                        onExpandedChange={(expanded) =>
                                            setExpandedFilterId(expanded ? field.id : null)
                                        }
                                        onRemove={() => {
                                            handleFilterChange(filterId, []);
                                            handleFilterVisibilityChange(filterId, false);
                                        }}
                                    />
                                </div>
                            );
                        })}
                        <div style={{ padding: '8px' }}>
                            <AddFilter
                                availableFilters={inferredFieldConfig.filter((field) => !visibleFilterIds.includes(field.id))}
                                onAddFilter={(filterId) => {
                                    handleFilterVisibilityChange(filterId, true);
                                }}
                            />
                        </div>
                    </div>
                </AnalyticsCard>

                {/* Old Sections */}
                {loading && (
                    <AnalyticsCard>
                        <div style={{
                            padding: '32px 0',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '16px'
                        }}>
                            <Spin indicator={antIcon} />
                            <div style={{ color: '#6b7280', marginTop: 8 }}>
                                Loading analytics data... {loadedRecordCount > 0 ? `(${loadedRecordCount} records)` : ''}
                            </div>
                            <Button type="link" onClick={handleStopLoading}>Stop loading</Button>
                        </div>
                    </AnalyticsCard>
                )}
                {error && (
                    <AnalyticsCard style={{ backgroundColor: '#fff2f0', borderColor: '#ffccc7' }}>
                        <div style={{ color: '#cf1322' }}>Error: {error}</div>
                    </AnalyticsCard>
                )}
                {!loading && !error && data && (
                    <AnalyticsCard>
                        <ChartSection
                            data={filteredData}
                            availableMetrics={metricsForChart}
                            selectedMetrics={selectedMetrics}
                            onMetricAdd={handleMetricAdd}
                            onMetricRemove={handleMetricRemove}
                            onMetricToggleAverage={handleMetricToggleAverage}
                            granularity={chartGranularity}
                            onGranularityChange={handleGranularityChange}
                        />
                    </AnalyticsCard>
                )}

                <div  ref={callListRef}>
                <AnalyticsCard>
                {loading && (
                        <div style={{
                            padding: '32px 0',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '16px'
                        }}>
                            <Spin indicator={antIcon} />
                            <div style={{ color: '#6b7280', marginTop: 8 }}>
                                Loading analytics data... {loadedRecordCount > 0 ? `(${loadedRecordCount} records)` : ''}
                            </div>
                            <Button type="link" onClick={handleStopLoading}>Stop loading</Button>
                        </div>
                )}
                {!loading && !error && data && (
                        <CallList
                            calls={filteredData}
                            config={currentConfig.callList}
                            onConfigChange={stableCallbacks.current.handleCallListConfigChange}
                            fieldConfig={inferredFieldConfig}
                        />
                )}
                </AnalyticsCard>
                </div>
                {loading && (

                        <div style={{
                            padding: '32px 0',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '16px'
                        }}>
                            <Spin indicator={antIcon} />
                            <div style={{ color: '#6b7280', marginTop: 8 }}>
                                Loading analytics data... {loadedRecordCount > 0 ? `(${loadedRecordCount} records)` : ''}
                            </div>
                            <Button type="link" onClick={handleStopLoading}>Stop loading</Button>
                        </div>

                )}
                {!loading && !error && data && (
                    <ScorecardsSection
                        data={filteredData}
                        config={currentConfig.scorecards}
                        onConfigChange={handleScorecardConfigChange}
                        onFilterChange={handleScorecardFilterChange}
                        exportModalVisible={scorecardExportModalVisible}
                    />
                )}
            </Space>

            <SecureComponent requiredRoles={RBAC_ROLES.SUPER_ADMIN}>
                <VoiceAnalystWidget
                    context={{
                        defaultPropertyName: currentConfig?.defaultPropertyName,
                        dateRange: dateRange,
                        activeFilters: activeFilters,
                        selectedCompanyNames: selectedCompanyNames,
                        totalCalls: data?.length,
                        filteredCalls: filteredData?.length
                    }}
                    showCallModal={showCallModal}
                    showReportModal={handleShowReportModal}
                    showCallsList={handleAiCallsList}
                    onAnalystResponse={(response) => {
                        console.log('AI Analyst provided insight:', response);
                    }}
                />
            </SecureComponent>

            <CustomReportModal
                visible={aiReportModalVisible}
                onClose={handleCloseAiReportModal}
                mode="direct"
                directReportData={aiReportData}
                title={aiReportTitle}
            />


            {/* Configuration Modal for CallList (if needed) */}
            <ViewConfigModal
                visible={isConfigModalVisible}
                onClose={() => setIsConfigModalVisible(false)}
                config={currentConfig.callList}
                onConfigChange={handleCallListConfigChange}
                fieldConfig={inferredFieldConfig}
            />

            <CustomFieldsManager
                isVisible={customFieldsModalVisible}
                onClose={() => setCustomFieldsModalVisible(false)}
                customFields={currentConfig.customFields || []}
                onSave={handleCustomFieldsUpdate}
                calls={data}
                sampleRecord={data && data.length > 0 ? data[0] : null}
            />
            {callExportModalVisible && (
                <Modal
                    title="Export Call Data"
                    visible={callExportModalVisible}
                    onCancel={() => setCallExportModalVisible(false)}
                    footer={null}
                    width={520}
                >
                    {/* Render the same content as your call list's DownloadDataButton modal */}
                    <DownloadDataButton
                        data={data}
                        filteredData={filteredData}
                        callListConfig={currentConfig.callList}
                        fieldConfig={inferredFieldConfig}
                        embedded={true}
                        onFinish={() => setCallExportModalVisible(false)}
                    />
                </Modal>
            )}
        </div>
    );
};

export default AnalyticsView;
